/**
 * ScheduleDraftWorkspaceSidebar — 编辑器 Drawer 组装层。
 *
 * 设计文档 → docs/20_workspace_ux/21_workspace_layout.md
 * 骨架关联 → components/ScheduleDraftFlightDetailPanel.jsx (详情面板),
 *             components/ScheduleDraftTrackConfigPanel.jsx (轨道与参考),
 *             components/ScheduleDraftTrackPanel.jsx (历史快照)
 */

import React from "react";
import { Card, Segmented, Typography } from "antd";
import useScheduleDraftEditorWorkspace from "../../hooks/useScheduleDraftEditorWorkspace";
import { HISTORY_TRACK_TYPE, buildScheduleTrackKey, getDefaultEditingTrack } from "../../lib/trackUtils";
import ScheduleDraftFlightDetailPanel from "../ScheduleDraftFlightDetailPanel";
import ScheduleDraftTrackConfigPanel from "../ScheduleDraftTrackConfigPanel";
import AgentSessionPanel from "./AgentSessionPanel";
import ScheduleDraftOperationHistoryPanel from "./ScheduleDraftOperationHistoryPanel";
import ScheduleDraftTransportDemandPanel from "./ScheduleDraftTransportDemandPanel";
import ScheduleDraftTrackPanel from "../ScheduleDraftTrackPanel";

const { Text } = Typography;

const PRIMARY_DRAWER_PANEL_OPTIONS = [
    { label: "当前选中", value: "selection" },
    { label: "轨道与参考", value: "reference" },
    { label: "需求与覆盖", value: "demands" },
];

const SECONDARY_DRAWER_PANEL_OPTIONS = [
    { label: "操作历史", value: "operations" },
    { label: "Agent 方案", value: "agents" },
    { label: "历史快照", value: "history" },
];

function hasPanelValue(options, value) {
    return options.some((option) => option.value === value);
}

const ScheduleDraftWorkspaceSidebar = () => {
    const {
        draftId,
        data: { draft, selectedItem, agentSessions, isFetchingAgentSessions, agentSessionsError },
        ui: {
            sidebarPanelMode,
            setSidebarPanelMode,
            showEditableTracks,
            setShowEditableTracks,
            showDemandReferences,
            setShowDemandReferences,
            visibleReferenceTrackIds,
            referenceTrackItems,
            toggleReferenceTrack,
            addReferenceTrack,
            moveReferenceTrack,
            removeReferenceTrack,
            openSourceTrackDialog,
            setObservationMode,
            setSourceTrack,
            currentEditingTrackId,
        },
        mutations: {
            handleSaveTrackAsHistory,
            handleRestoreTrack,
            handleDeleteTrack,
            handleDeleteFlight,
            savingTrackAsHistory,
            restoringTrack,
            restoringTrackId,
            deletingFlight,
            deletingTrack,
            deletingTrackId,
        },
        editPipeline,
        permissions: { canManageTrack, canUpdateFlight, canDeleteFlight, canDeleteTrack },
    } = useScheduleDraftEditorWorkspace();

    const currentTrackId = currentEditingTrackId || getDefaultEditingTrack(draft)?.id || draft?.default_track_id;
    const historyTracks = (draft?.tracks || []).filter((track) => track?.track_type === HISTORY_TRACK_TYPE);

    const handleOpenAgentTrack = (trackId) => {
        addReferenceTrack(trackId);
        setObservationMode("timeline");
        setSourceTrack(buildScheduleTrackKey(trackId));
    };

    const renderActivePanel = () => {
        if (sidebarPanelMode === "selection") {
            return (
                <ScheduleDraftFlightDetailPanel
                    selectedItem={selectedItem}
                    draftId={draftId}
                    executeCommand={editPipeline.executeCommand}
                    onDeleteFlight={handleDeleteFlight}
                    deletingFlight={deletingFlight}
                    canUpdateFlight={canUpdateFlight}
                    canDeleteFlight={canDeleteFlight}
                />
            );
        }

        if (sidebarPanelMode === "reference") {
            return (
                <ScheduleDraftTrackConfigPanel
                    title="轨道与参考"
                    tracks={historyTracks}
                    showEditableTracks={showEditableTracks}
                    showDemandReferences={showDemandReferences}
                    visibleReferenceTrackIds={visibleReferenceTrackIds}
                    referenceTrackItems={referenceTrackItems}
                    onToggleEditableTracks={setShowEditableTracks}
                    onToggleDemandReferences={setShowDemandReferences}
                    onToggleReferenceTrack={toggleReferenceTrack}
                    onMoveReferenceTrack={moveReferenceTrack}
                    onRemoveReferenceTrack={removeReferenceTrack}
                    onOpenAddReferenceTrack={() => openSourceTrackDialog({ referenceOnly: true })}
                />
            );
        }

        if (sidebarPanelMode === "operations") {
            return <ScheduleDraftOperationHistoryPanel />;
        }

        if (sidebarPanelMode === "demands") {
            return <ScheduleDraftTransportDemandPanel />;
        }

        if (sidebarPanelMode === "history") {
            return (
                <ScheduleDraftTrackPanel
                    title="历史快照"
                    tracks={draft?.tracks || []}
                    currentTrackId={currentTrackId}
                    expandedTrackIds={visibleReferenceTrackIds}
                    onSaveTrackAsHistory={handleSaveTrackAsHistory}
                    onRestoreTrack={handleRestoreTrack}
                    onDeleteTrack={handleDeleteTrack}
                    canManageTrack={canManageTrack}
                    canDeleteTrack={canDeleteTrack}
                    savingTrackAsHistory={savingTrackAsHistory}
                    restoringTrackId={restoringTrackId}
                    restoringTrack={restoringTrack}
                    deletingTrack={deletingTrack}
                    deletingTrackId={deletingTrackId}
                />
            );
        }

        if (sidebarPanelMode === "agents") {
            return (
                <AgentSessionPanel
                    sessions={agentSessions}
                    tracks={draft?.tracks || []}
                    isFetching={isFetchingAgentSessions}
                    error={agentSessionsError}
                    onOpenTrack={handleOpenAgentTrack}
                />
            );
        }

        return null;
    };

    return (
        <div className="schedule-draft-side-column">
            <Card title="上下文辅助台" className="schedule-draft-side-card schedule-draft-sidebar-shell" size="small">
                <div className="schedule-draft-sidebar-mode-groups">
                    <Text type="secondary" className="schedule-draft-sidebar-mode-group-label">常用</Text>
                    <Segmented
                        className="schedule-draft-sidebar-mode-switcher"
                        value={hasPanelValue(PRIMARY_DRAWER_PANEL_OPTIONS, sidebarPanelMode) ? sidebarPanelMode : "__none"}
                        onChange={setSidebarPanelMode}
                        options={PRIMARY_DRAWER_PANEL_OPTIONS}
                        block
                    />
                    <Text type="secondary" className="schedule-draft-sidebar-mode-group-label">辅助</Text>
                    <Segmented
                        className="schedule-draft-sidebar-mode-switcher"
                        value={hasPanelValue(SECONDARY_DRAWER_PANEL_OPTIONS, sidebarPanelMode) ? sidebarPanelMode : "__none"}
                        onChange={setSidebarPanelMode}
                        options={SECONDARY_DRAWER_PANEL_OPTIONS}
                        block
                    />
                </div>
            </Card>
            {renderActivePanel()}
        </div>
    );
};

export default ScheduleDraftWorkspaceSidebar;
